import React, { useState } from 'react';
import { Mail, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../api';
import { toast } from 'sonner';

const STORAGE_KEY = 'fnl_verify_banner_dismissed_at';
const DISMISS_HOURS = 12;

const EmailVerifyBanner = () => {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    try {
      const ts = localStorage.getItem(STORAGE_KEY);
      if (!ts) return false;
      return (Date.now() - Number(ts)) < DISMISS_HOURS * 3600 * 1000;
    } catch (err) {
      // localStorage can throw in Safari private mode — just show the banner
      console.debug('localStorage read failed:', err?.message);
      return false;
    }
  });
  const [busy, setBusy] = useState(false);

  if (!user || user.email_verified || dismissed) return null;

  const resend = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post('/auth/resend-verification');
      if (r.data?.already_verified) {
        toast.success('Already verified — refresh to update.');
      } else {
        toast.success('Verification email sent', {
          description: `Check ${user.email} for the link.`,
        });
      }
    } catch (err) {
      toast.error('Could not resend', { description: err.response?.data?.detail || '' });
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (err) {
      console.debug('localStorage write failed:', err?.message);
    }
    setDismissed(true);
  };

  return (
    <div className="mb-5 rounded-xl border border-yellow-400/40 bg-yellow-400/10 px-4 py-3 flex items-center gap-3" data-testid="verify-email-banner">
      <Mail className="w-5 h-5 text-yellow-300 shrink-0" />
      <div className="flex-1 text-sm text-yellow-50">
        <span className="font-semibold">Verify your email</span>{' '}
        <span className="text-yellow-100/85">— check {user.email} for the confirmation link. Verified members rank higher in browse.</span>
      </div>
      <button
        type="button"
        onClick={resend}
        disabled={busy}
        className="text-yellow-300 hover:text-white text-xs font-bold uppercase tracking-wider whitespace-nowrap"
        data-testid="verify-email-resend"
      >
        {busy ? 'Sending...' : 'Resend'}
      </button>
      <button
        type="button"
        onClick={dismiss}
        className="text-yellow-200/70 hover:text-white p-1"
        aria-label="Dismiss"
        data-testid="verify-email-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default EmailVerifyBanner;
